<script setup lang="ts">

import { defineProps, ref, onMounted, onUnmounted, defineEmits, computed, watch } from 'vue'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore.ts'
import ModalCloseButton from '@/components/Elements/Buttons/ModalCloseButton.vue'

const props = defineProps({
  title: String,
  preventClose: {
    type: Boolean,
    default: false
  },
  useMinimizeAnimation: {
    type: Boolean,
    default: false
  },
  minimizeFlagKey: {
    type: String,
    default: 'tutorial_hidden'
  }
});

const emit = defineEmits(['close'])

const playerFlagsStore = usePlayerFlagsStore()
const modalRef = ref<HTMLElement>()
const titleBarRef = ref<HTMLElement>()
const isDragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const modalPosition = ref({ x: 0, y: 0 })
const isMinimized = ref(false)
const isAnimating = ref(false)
const isMinimizingAnimation = ref(false)
const isRestoringAnimation = ref(false)
const isLocalChange = ref(false)

const nonClosableModel = computed(() => {
  const currentTutorialStep = playerFlagsStore.getFlag('tutorial_current_step') ?? 'tutorial_intro'
  return currentTutorialStep === 'tutorial_intro' || currentTutorialStep === 'tutorial_start'
})

// Calculate the target position for the footer icon dynamically
const animationStyles = computed(() => {
  if (!modalRef.value) {
    return {
      '--target-x': '0px',
      '--target-y': '0px'
    }
  }
  
  // Calculate distance to bottom-right corner of footer
  // The icon is now in the footer, so we target the bottom-right area
  const iconX = window.innerWidth - 60 // right side of footer
  const iconY = window.innerHeight - 20 // footer height
  
  let currentX, currentY
  
  if (modalPosition.value.x === 0 && modalPosition.value.y === 0) {
    // Modal is centered
    currentX = window.innerWidth / 2
    currentY = window.innerHeight / 2
  } else {
    // Modal has been dragged
    currentX = modalPosition.value.x + (modalRef.value?.offsetWidth || 0) / 2
    currentY = modalPosition.value.y + (modalRef.value?.offsetHeight || 0) / 2
  }
  
  const deltaX = iconX - currentX
  const deltaY = iconY - currentY
  
  return {
    '--target-x': `${deltaX}px`,
    '--target-y': `${deltaY}px`,
    '--restore-transform': modalPosition.value.x === 0 && modalPosition.value.y === 0 ? 'translate(-50%, -50%)' : 'translate(0, 0)'
  }
})

const closeModal = async () => {
  if (props.useMinimizeAnimation) {
    await minimizeToIcon()
  } else {
    emit('close')
  }
}

const minimizeToIcon = async () => {
  if (isAnimating.value) return

  isAnimating.value = true
  isMinimizingAnimation.value = true

  // Wait for animation to complete
  setTimeout(async () => {
    isMinimized.value = true
    isMinimizingAnimation.value = false
    isAnimating.value = false

    // Mark as local change and save state to player flags
    isLocalChange.value = true
    await playerFlagsStore.setFlag(props.minimizeFlagKey, 'true')
    // Reset local change flag after a brief delay
    setTimeout(() => { isLocalChange.value = false }, 50)
  }, 500)
}

const restoreFromIcon = async () => {
  if (isAnimating.value) return

  isAnimating.value = true
  isRestoringAnimation.value = true
  isMinimized.value = false

  // Mark as local change and save state to player flags
  isLocalChange.value = true
  await playerFlagsStore.setFlag(props.minimizeFlagKey, 'false')
  // Reset local change flag after a brief delay
  setTimeout(() => { isLocalChange.value = false }, 50)

  // Wait for animation to complete
  setTimeout(() => {
    isRestoringAnimation.value = false
    isAnimating.value = false
  }, 500)
}

const handleRestoreTutorial = () => {
  if (props.useMinimizeAnimation && isMinimized.value) {
    restoreFromIcon()
  }
}

const startDrag = (e: MouseEvent) => {
  if (!modalRef.value) return
  
  isDragging.value = true
  const rect = modalRef.value.getBoundingClientRect()
  dragOffset.value = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }
  
  document.addEventListener('mousemove', drag)
  document.addEventListener('mouseup', stopDrag)
}

const drag = (e: MouseEvent) => {
  if (!isDragging.value || !modalRef.value) return
  
  const newX = e.clientX - dragOffset.value.x
  const newY = e.clientY - dragOffset.value.y
  
  // Keep modal within viewport bounds
  const maxX = window.innerWidth - modalRef.value.offsetWidth
  const maxY = window.innerHeight - modalRef.value.offsetHeight
  
  modalPosition.value = {
    x: Math.max(0, Math.min(newX, maxX)),
    y: Math.max(0, Math.min(newY, maxY))
  }
}

const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', drag)
  document.removeEventListener('mouseup', stopDrag)
}

// Watch for flag changes from the backend
watch(
  () => playerFlagsStore.getFlag(props.minimizeFlagKey),
  (newValue, _oldValue) => {
    // Only react to changes if they're not from local actions
    if (!isLocalChange.value && props.useMinimizeAnimation) {
      const shouldBeMinimized = newValue === 'true'

      // If backend sets flag to false (show modal) and we're currently minimized
      if (!shouldBeMinimized && isMinimized.value) {
        // Show the modal with restore animation
        restoreFromIcon()
      }
      // If backend sets flag to true (hide modal) and we're currently visible
      else if (shouldBeMinimized && !isMinimized.value) {
        // Hide the modal with minimize animation
        minimizeToIcon()
      }
    }
  }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)

  // Listen for restore tutorial event from footer
  window.addEventListener('restore-tutorial', handleRestoreTutorial)

  // Check initial state from player flags
  if (props.useMinimizeAnimation) {
    const flagValue = playerFlagsStore.getFlag(props.minimizeFlagKey)
    isMinimized.value = flagValue === 'true'
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('restore-tutorial', handleRestoreTutorial)
  document.removeEventListener('mousemove', drag)
  document.removeEventListener('mouseup', stopDrag)
})

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && !props.preventClose) {
    closeModal()
  }
}

</script>

<template>
  <div>
    <!-- Main Modal -->
    <div
      v-if="!isMinimized"
      class="flex overflow-x-hidden fixed w-full inset-0 h-full md:h-auto md:inset-0 z-[100]"
      :class="{ 
        'bg-black bg-opacity-50 pointer-events-auto': nonClosableModel,
        'pointer-events-none': !nonClosableModel
      }"
    >
      <div
        ref="modalRef"
        class="absolute w-full max-w-xl max-h-full pointer-events-auto"
        :class="{
          'minimize-animation': isMinimizingAnimation,
          'restore-animation': isRestoringAnimation
        }"
        :style="{
          left: modalPosition.x === 0 && modalPosition.y === 0 ? '50%' : modalPosition.x + 'px',
          top: modalPosition.x === 0 && modalPosition.y === 0 ? '50%' : modalPosition.y + 'px',
          transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
          ...animationStyles
        }"
      >
        <div class="relative rounded-lg shadow drop-shadow bg-metal-950 m-1 md:m-3" style="box-shadow: 0 0 50px rgba(0, 0, 0, 0.8), 0 25px 50px -12px rgba(0, 0, 0, 0.25);">
          <div
            ref="titleBarRef"
            class="flex items-center justify-between p-3 md:p-3 rounded-t cursor-move select-none modal-header"
            @mousedown="startDrag"
          >
            <h3 class="text-metal-300 text-base font-semibold">
              {{ props.title }}
            </h3>
            <ModalCloseButton v-if="!preventClose && !nonClosableModel" @click.stop="closeModal">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"></path>
              </svg>
            </ModalCloseButton>
          </div>
          <div class="overflow-y-auto max-h-[calc(100vh-200px)] text-metal-400 border-l border-r border-b border-metal-900 rounded-b-lg">
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-header {
  background: linear-gradient(to right, theme('colors.metal.800'), theme('colors.metal.900'));
}

@media (max-width: 768px) {
  .max-h-full {
    max-height: calc(100vh - 100px);
  }
}

// Minimize animation
.minimize-animation {
  animation: minimize-to-icon 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.restore-animation {
  animation: restore-from-icon 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes minimize-to-icon {
  to {
    transform: translate(var(--target-x), var(--target-y)) scale(0.1);
    opacity: 0;
  }
}

@keyframes restore-from-icon {
  from {
    transform: translate(var(--target-x), var(--target-y)) scale(0.1);
    opacity: 0;
  }
  to {
    transform: var(--restore-transform, translate(-50%, -50%)) scale(1);
    opacity: 1;
  }
}
</style>